import CalendarIcon from '@assets/icons/calendar.svg';
import React from 'react';
import TruckIcon from '@assets/icons/truck-small.svg';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FulfillmentName } from '@/types';
import { StyleProp, ViewStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { Timeslot } from '@domain/fulfillment-settings/types/types';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  fulfillmentDate: string | null;
  fulfillmentType: FulfillmentName | null;
  loading?: boolean;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  timeslot: Timeslot | null;
  trackingData: TrackingData;
}

const FULFILLMENT_NAME_TO_ICON_MAP: {
  [name in FulfillmentName]: React.ComponentType<SvgProps>;
} = {
  [FulfillmentName.Delivery]: CalendarIcon,
  [FulfillmentName.Pickup]: CalendarIcon,
  [FulfillmentName.SecondRunDelivery]: CalendarIcon,
  [FulfillmentName.UpsGround]: TruckIcon,
  [FulfillmentName.UpsNextDay]: TruckIcon,
  [FulfillmentName.Ups_2Day]: TruckIcon,
};

export const FulfillmentButton: React.FC<Props> = ({
  containerStyle,
  fulfillmentDate,
  fulfillmentType,
  loading,
  onPress,
  style,
  timeslot,
  trackingData,
}) => {
  const { allowDeliveryDateEdit, hasResponse } = useNewOrderContext();
  const [styles, theme] = useStyles();

  const orderAvailability = useSupplierOrderAvailability();

  const fulfillmentDisplayName = orderAvailability.fulfillmentDisplayName;

  if (!fulfillmentDate || !fulfillmentType || !hasResponse) {
    return null;
  }

  const IconComponent =
    FULFILLMENT_NAME_TO_ICON_MAP[fulfillmentType] || CalendarIcon;

  const formattedDisplayString =
    orderAvailability.getSelectedFulfillmentDateTimeString(
      fulfillmentDate,
      timeslot,
    );

  return (
    <Button
      containerStyle={containerStyle}
      disabled={!allowDeliveryDateEdit}
      leftElement={
        <IconComponent height={20} stroke={theme.colors.primary} width={20} />
      }
      leftElementStyle={styles.leftElement}
      loading={loading}
      onPress={onPress}
      rounded
      size="xsmall"
      style={[styles.buttonStyle, style]}
      testID="fulfillment-select-button"
      textProps={{
        numberOfLines: 1,
      }}
      textStyle={styles.buttonText}
      trackingData={trackingData}
      type="secondary"
    >
      {translateFulfillmentType(
        fulfillmentType || FulfillmentName.Delivery,
        'fulfillment_header.type',
        fulfillmentDisplayName,
        {
          date: formattedDisplayString,
        },
      )}
    </Button>
  );
};

const useStyles = createStyles((theme) => ({
  buttonStyle: {
    backgroundColor: theme.colors.background,
  },
  buttonText: {
    color: theme.colors.primary,
    flexShrink: 1,
    fontSize: 14,
  },
  leftElement: {
    marginRight: 8,
  },
}));
